import {
    ComponentPropsWithoutRef,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FieldArrayWithId, UseFieldArrayReturn, useFieldArray } from 'react-hook-form';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import * as Draggable from '@givebutter/primitives/draggable';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import * as Sortable from '@givebutter/primitives/sortable';
import {
    faArchive,
    faEdit,
    faPenField,
    faPlus,
    faQuestionCircle,
    faTrash,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { EphemeralTicketId } from './EphemeralTicketId';
import { TicketFormData } from './types';
import { useCurrentTicket } from './useCurrentTicket';
import { TicketRemoveCustomFieldAlert } from 'assets/js/pages/campaign-settings/campaign-event/TicketRemoveCustomFieldAlert';
import { TicketCustomFieldFormValues } from 'assets/js/pages/campaign-settings/campaign-event/types';
import * as CustomFields from 'components/custom-fields';
import { toFriendlyType, typeIcon } from 'components/custom-fields/helpers';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import { cloneDeep, partition } from 'lodash';
import { twJoin } from 'tailwind-merge';
import { CustomField } from 'types/domain';
import { CustomFieldType } from 'types/filter';

interface TicketCustomFieldFormContainerProps extends ComponentPropsWithoutRef<'div'> {}

const TicketCustomFieldFormContainer = (props: TicketCustomFieldFormContainerProps) => {
    const { className = '', ...rest } = props;

    return <div className={`flex flex-col gap-2 ${className}`} {...rest} />;
};

interface TicketCustomFieldFormContext {
    ticketCustomFieldArray: UseFieldArrayReturn<TicketFormData, 'custom_fields', 'key'>;
}

const TicketCustomFieldFormContext = createContext<TicketCustomFieldFormContext | undefined>(
    undefined,
);

interface TicketCustomFieldFormProviderProps {
    children: ReactNode;
}

const useTicketCustomFieldFormContext = () => {
    const context = useContext(TicketCustomFieldFormContext);
    if (!context) {
        throw new Error(
            'useTicketCustomFieldFormContext must be used within a TicketCustomFieldFormProvider',
        );
    }
    return context;
};

const TicketCustomFieldFormProvider = (props: TicketCustomFieldFormProviderProps) => {
    const { children } = props;
    const { control } = Form.useGivebutterFormContext<TicketFormData>();
    const ticketCustomFieldArray = useFieldArray({
        control,
        name: 'custom_fields',
        keyName: 'key',
    });
    const value = useMemo(() => ({ ticketCustomFieldArray }), [ticketCustomFieldArray]);
    return (
        <TicketCustomFieldFormContext.Provider value={value}>
            {children}
        </TicketCustomFieldFormContext.Provider>
    );
};

const TicketFormCustomFieldsHeader = () => {
    const {
        formState: { errors },
    } = Form.useGivebutterFormContext<TicketFormData>();
    const { ticketCustomFieldArray } = useTicketCustomFieldFormContext();
    const { fields } = ticketCustomFieldArray;

    return (
        <>
            <div className={'flex items-center justify-between'}>
                <div className={'flex items-center gap-x-1'}>
                    <h2 className={'text-base font-medium'}>Custom fields</h2>
                    <Tooltip.Root>
                        <Tooltip.Trigger className={'cursor-pointer'} asChild>
                            <FontAwesomeIcon
                                icon={faQuestionCircle}
                                className={'h-3.5 w-3.5 text-gray-500'}
                            />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content side={'top'} sideOffset={2} inverse>
                                <Tooltip.Arrow inverse />
                                <p className={'text-sm leading-5'}>
                                    Custom fields can gather specific details from supporters during
                                    ticket purchases, such as shirt size or meal preferences.
                                </p>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
                {!!fields.length && <TicketCustomFieldFormMenu />}
            </div>
            {errors?.custom_fields && (
                <p className={'text-sm font-medium text-red-500'}>
                    Some custom fields had errors. Edit the highlighted fields to see details.
                </p>
            )}
        </>
    );
};

type IndexedTicketCustomFieldFormValue = FieldArrayWithId<
    TicketFormData,
    'custom_fields',
    'key'
> & { index: number };

interface CustomFieldSortGroupProps {
    allTickets: boolean;
}

const TicketCustomFieldSortGroup = (props: CustomFieldSortGroupProps) => {
    const { allTickets } = props;
    const { ticketCustomFieldArray } = useTicketCustomFieldFormContext();
    const { move, remove, fields } = ticketCustomFieldArray;

    const { data: ticket } = useCurrentTicket();
    const customFields = ticket?.custom_fields ?? [];

    const groupedFields = fields
        .reduce((acc, field, i) => {
            acc.push({ ...field, index: i });

            return acc;
        }, [] as IndexedTicketCustomFieldFormValue[])
        .filter(field => field.all_tickets === allTickets);

    const {
        getValues,
        formState: { errors },
    } = Form.useGivebutterFormContext<TicketFormData>();

    const handleDragEnd = (event: Draggable.DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = fields.findIndex(field => field.key === active.id);
            const newIndex = fields.findIndex(field => field.key === over?.id);

            move(oldIndex, newIndex);
        }
    };

    return (
        <Draggable.Root onDragEnd={handleDragEnd}>
            <Sortable.Root items={groupedFields.map(f => f.key)}>
                {groupedFields.map(({ index, ...field }) => {
                    const currentFormValue = getValues(`custom_fields.${index}`);
                    const persistedCustomField = customFields?.find(f => f.id === field.id) ?? null;

                    if (!currentFormValue) {
                        return null;
                    }

                    return (
                        <Collapsible.Root
                            key={field.key}
                            className={'group'}
                            defaultOpen={currentFormValue?.id === EphemeralTicketId.New}
                        >
                            <Sortable.Item
                                itemId={field.key}
                                className={'group-radix-state-open:hidden'}
                            >
                                <Sortable.Card
                                    className={twJoin(
                                        'h-10 justify-between pr-2',
                                        errors?.['custom_fields']?.[index] &&
                                            'border-red-500 hover:border-red-500',
                                    )}
                                >
                                    <Sortable.Grip />
                                    <div className={'flex max-w-96 items-center gap-x-2'}>
                                        <FontAwesomeIcon
                                            icon={typeIcon(currentFormValue.type)}
                                            className={'h-3.5 w-3.5 text-gray-900'}
                                        />
                                        <p className={'line-clamp-1'}>{currentFormValue.title}</p>
                                    </div>
                                    <div className={'hidden group-hover:flex'}>
                                        <Collapsible.Trigger asChild>
                                            <Button
                                                variant="transparent"
                                                shape={'square'}
                                                size={'sm'}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                    className={'h-3.5 w-3.5 text-gray-500'}
                                                />
                                            </Button>
                                        </Collapsible.Trigger>
                                        <TicketRemoveCustomFieldAlert
                                            open={persistedCustomField ? undefined : false}
                                            customField={persistedCustomField}
                                            onConfirm={() => remove(index)}
                                        >
                                            <Button
                                                variant="transparent"
                                                size={'sm'}
                                                shape={'square'}
                                                onClick={
                                                    persistedCustomField
                                                        ? undefined
                                                        : () => remove(index)
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faXmark}
                                                    className={'h-3.5 w-3.5 text-gray-500'}
                                                />
                                            </Button>
                                        </TicketRemoveCustomFieldAlert>
                                    </div>
                                </Sortable.Card>
                            </Sortable.Item>
                            <Collapsible.Content>
                                <TicketCustomFieldArrayInput
                                    index={index}
                                    formId={field.id}
                                    customField={persistedCustomField}
                                />
                            </Collapsible.Content>
                        </Collapsible.Root>
                    );
                })}
            </Sortable.Root>
        </Draggable.Root>
    );
};

interface TicketCustomFieldArrayInputProps {
    index: number;
    formId: number;
    customField: CustomField | null;
}

const TicketCustomFieldArrayInput = (props: TicketCustomFieldArrayInputProps) => {
    const { index, formId, customField } = props;
    const { ticketCustomFieldArray } = useTicketCustomFieldFormContext();
    const { fields, move, remove, update } = ticketCustomFieldArray;
    const methods = Form.useFormContext<TicketFormData>();
    const { getValues, setFocus } = methods;
    const isNew = formId === EphemeralTicketId.New;
    const isStaged = formId === EphemeralTicketId.Staging;
    const [snapshot, setSnapshot] = useState<TicketCustomFieldFormValues | undefined>();
    const { data: ticket } = useCurrentTicket();

    // Using snapshot b/c as soon as a saved field is toggled to all tickets,
    // the `getValues` changes this to true and doesn't let you toggle off
    // even though you haven't saved it yet.

    useEffect(() => {
        setFocus(`custom_fields.${index}.title`);
    }, [index, setFocus]);

    const onDone = () => {
        const values = getValues(`custom_fields.${index}`);

        update(index, {
            ...values,
            id: isNew ? EphemeralTicketId.Staging : values.id,
        });

        if (isNew) {
            return;
        }

        if (snapshot?.all_tickets !== values.all_tickets) {
            const endOfCampaignList = fields.findIndex(f => !f.all_tickets);

            move(index, values.all_tickets ? endOfCampaignList : fields.length - 1);
        }
    };

    const onCancel = () => {
        if (isNew) {
            remove(index);
        } else if (snapshot) {
            update(index, snapshot);
        }
    };

    return (
        <Button
            variant="outline"
            color="neutral"
            className={
                'bg-gray-25 hover:bg-gray-25 h-fit flex-col items-start gap-y-6 text-wrap p-6 text-start'
            }
            style={{
                fontWeight: 'unset',
            }}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.stopPropagation();
                    onDone();
                }
            }}
        >
            {!isNew && snapshot?.all_tickets && (
                <div className={'flex gap-3 rounded-lg border border-orange-300 bg-orange-50 p-3'}>
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className={'relative top-1 text-orange-500'}
                    />
                    <p className={'text-sm font-medium leading-5 text-orange-700'}>
                        Saved changes made to this custom field will be reflected on all associated
                        tickets.
                    </p>
                </div>
            )}

            <CustomFields.Root pathPrefix={`custom_fields.${index}`} customField={customField}>
                <TicketSnapshot index={index} onInit={setSnapshot} />
                <CustomFields.Title />
                <CustomFields.Description>
                    <CustomFields.FieldDescription>
                        Shows beneath input to aid supporters.
                    </CustomFields.FieldDescription>
                </CustomFields.Description>
                <CustomFields.Text />
                <CustomFields.Checkbox />
                <CustomFields.Option />
                <CustomFields.NumberForm />
                <Separator />
                <div className="flex flex-col gap-2">
                    <Label className="text-base">Configuration</Label>
                    <div className="flex flex-col gap-4">
                        <CustomFields.AdminOnly />
                        <CustomFields.AllTickets ticket={ticket} setSnapshot={setSnapshot} />
                        <CustomFields.Required>
                            <CustomFields.RequiredLabel>
                                {index && getValues(`custom_fields.${index}.type`) === 'checkbox'
                                    ? 'Opt-in required'
                                    : 'Required field'}
                            </CustomFields.RequiredLabel>
                            <CustomFields.FieldDescription>
                                {index && getValues(`custom_fields.${index}.type`) === 'checkbox'
                                    ? 'Require supporters to check the checkbox to proceed with their ticket purchase.'
                                    : 'Make this field required when purchasing this ticket.'}
                            </CustomFields.FieldDescription>
                        </CustomFields.Required>
                    </div>
                </div>
            </CustomFields.Root>
            <Form.Container className={'!flex-row justify-between gap-x-3'}>
                <div>
                    {!isNew && (
                        <TicketRemoveCustomFieldAlert
                            customField={customField}
                            onConfirm={() => remove(index)}
                        >
                            <Button
                                size={'sm'}
                                variant={'outline'}
                                color={'error'}
                                onClick={() => {
                                    !customField && remove(index);
                                }}
                            >
                                <FontAwesomeIcon icon={isStaged ? faTrash : faArchive} />
                                <span>{isStaged ? 'Remove' : 'Archive'}</span>
                            </Button>
                        </TicketRemoveCustomFieldAlert>
                    )}
                </div>

                <div className={'flex gap-3'}>
                    <Collapsible.Trigger asChild>
                        <Button
                            onClick={() => onCancel()}
                            size={'sm'}
                            variant={'outline'}
                            color={'neutral'}
                        >
                            Cancel
                        </Button>
                    </Collapsible.Trigger>
                    <Collapsible.Trigger asChild>
                        <Button variant="solid" onClick={() => onDone()} size={'sm'}>
                            Done
                        </Button>
                    </Collapsible.Trigger>
                </div>
            </Form.Container>
        </Button>
    );
};

const TicketCustomFieldFormEmpty = () => {
    return (
        <div className="flex justify-center rounded-xl border text-gray-600">
            <div className="flex max-w-sm flex-col items-center justify-center gap-6 p-12">
                <BoxIcon size="lg">
                    <FontAwesomeIcon icon={faPenField} size="lg" />
                </BoxIcon>
                <div className="flex flex-col gap-1">
                    <p className={'text-center font-medium text-gray-900'}>
                        No existing custom fields
                    </p>
                    <p className={'min-w-[350px] text-center text-sm text-gray-600'}>
                        Add custom fields to this ticket for your attendees to answer.
                    </p>
                </div>
                <TicketCustomFieldFormMenu />
            </div>
        </div>
    );
};

const TicketCustomFieldFormItems = () => {
    const { ticketCustomFieldArray } = useTicketCustomFieldFormContext();
    const { fields } = ticketCustomFieldArray;
    const [campaignFields, ticketFields] = partitionCustomFields(fields);

    return (
        <div className={'flex flex-col gap-2'}>
            {fields.length > 0 ? (
                <div className={'flex flex-col gap-4'}>
                    {ticketFields.length > 0 && (
                        <div className={'flex flex-col gap-1'}>
                            <p className={'text-sm font-medium'}>This ticket</p>
                            <TicketCustomFieldSortGroup allTickets={false} />
                        </div>
                    )}

                    {campaignFields.length > 0 && ticketFields.length > 0 && <Separator />}

                    {campaignFields.length > 0 && (
                        <div className={'flex flex-col gap-1'}>
                            <p className={'text-sm font-medium'}>All tickets</p>
                            <TicketCustomFieldSortGroup allTickets={true} />
                        </div>
                    )}
                </div>
            ) : (
                <TicketCustomFieldFormEmpty />
            )}
        </div>
    );
};

const TicketCustomFieldFormMenu = () => {
    const { ticketCustomFieldArray } = useTicketCustomFieldFormContext();
    const { append } = ticketCustomFieldArray;

    const appendNewValueOfType = (type: CustomFieldType) => append(generateDefaultField(type));

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <Button type={'button'} variant={'outline'} color={'neutral'} size={'sm'}>
                    <FontAwesomeIcon icon={faPlus} className={'h-4 w-4'} />
                    <span className={'font-medium'}>Add custom field</span>
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content align={'start'} sideOffset={2}>
                    <DropdownMenu.Item
                        onClick={() => appendNewValueOfType('text')}
                        className={'flex gap-x-3'}
                    >
                        <div
                            className={
                                'flex h-10 w-10 items-center justify-center rounded bg-gray-100'
                            }
                        >
                            <FontAwesomeIcon
                                icon={typeIcon('text')}
                                className={'h-4 w-4 text-gray-900'}
                            />
                        </div>
                        <div>
                            <p className={'text-sm font-medium'}>Text</p>
                            <p className={'text-sm text-gray-600'}>Short or long answer text box</p>
                        </div>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        onClick={() => appendNewValueOfType('number')}
                        className={'flex gap-x-3'}
                    >
                        <div
                            className={
                                'flex h-10 w-10 items-center justify-center rounded bg-gray-100'
                            }
                        >
                            <FontAwesomeIcon
                                icon={typeIcon('number')}
                                className={'h-4 w-4 text-gray-900'}
                            />
                        </div>
                        <div>
                            <p className={'text-sm font-medium'}>Number</p>
                            <p className={'text-sm text-gray-600'}>Input with numeric value</p>
                        </div>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        onClick={() => appendNewValueOfType('checkbox')}
                        className={'flex gap-x-3'}
                    >
                        <div
                            className={
                                'flex h-10 w-10 items-center justify-center rounded bg-gray-100'
                            }
                        >
                            <FontAwesomeIcon
                                icon={typeIcon('checkbox')}
                                className={'h-4 w-4 text-gray-900'}
                            />
                        </div>
                        <div>
                            <p className={'text-sm font-medium'}>Checkbox</p>
                            <p className={'text-sm text-gray-600'}>
                                For collecting “Yes” or “No” answers
                            </p>
                        </div>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        onClick={() => appendNewValueOfType('select')}
                        className={'flex gap-x-3'}
                    >
                        <div
                            className={
                                'flex h-10 w-10 items-center justify-center rounded bg-gray-100'
                            }
                        >
                            <FontAwesomeIcon
                                icon={typeIcon('select')}
                                className={'h-4 w-4 text-gray-900'}
                            />
                        </div>
                        <div>
                            <p className={'text-sm font-medium'}>Option</p>
                            <p className={'text-sm text-gray-600'}>
                                Single or multi-select dropdown
                            </p>
                        </div>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        onClick={() => appendNewValueOfType('file')}
                        className={'flex gap-x-3'}
                    >
                        <div
                            className={
                                'flex h-10 w-10 items-center justify-center rounded bg-gray-100'
                            }
                        >
                            <FontAwesomeIcon
                                icon={typeIcon('file')}
                                className={'h-4 w-4 text-gray-900'}
                            />
                        </div>
                        <div>
                            <p className={'text-sm font-medium'}>File</p>
                            <p className={'text-sm text-gray-600'}>Upload a file</p>
                        </div>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

const partitionCustomFields = (
    fields: FieldArrayWithId<TicketFormData, 'custom_fields', 'key'>[],
) => partition(fields, field => field.all_tickets);

interface TicketSnapshotProps {
    index: number;
    onInit?: (snapshot: TicketCustomFieldFormValues) => void;
}

const TicketSnapshot = (props: TicketSnapshotProps) => {
    const { index, onInit } = props;
    const { getValues } = Form.useFormContext<TicketFormData>();
    const snapshotRef = useRef(cloneDeep(getValues(`custom_fields.${index}`)));

    useEffect(() => {
        onInit?.(snapshotRef.current);
    }, [onInit]);

    return <></>;
};

const generateDefaultField = (type: CustomFieldType) => {
    const answers = toFriendlyType(type) === 'option' ? [] : null;

    return {
        id: EphemeralTicketId.New,
        title: '',
        description: '',
        defaultResponse: '',
        type: type || 'text',
        required: false,
        answers,
        all_tickets: false,
    };
};

const Root = TicketCustomFieldFormProvider;
const Container = TicketCustomFieldFormContainer;
const Header = TicketFormCustomFieldsHeader;
const SortGroup = TicketCustomFieldSortGroup;
const Empty = TicketCustomFieldFormEmpty;
const Items = TicketCustomFieldFormItems;

export {
    useTicketCustomFieldFormContext,
    //
    Root,
    Container,
    Header,
    SortGroup,
    Empty,
    Items,
    //
    TicketCustomFieldFormProvider,
    TicketCustomFieldFormContainer,
    TicketFormCustomFieldsHeader,
    TicketCustomFieldSortGroup,
    TicketCustomFieldFormEmpty,
    TicketCustomFieldFormItems,
};
